
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getCandidateProfile,
  updateCandidateProfile,
  uploadCandidateProfileVideo,
  uploadCandidateProfileResume,
} from "../api/candidateProfile.api";
import type {
  CandidateProfileData,
  ProfileVideoData,
  ProfileResumeData,
} from "../types/candidateProfile.types";

type ProfileUpdatePayload = Partial<
  Pick<
    CandidateProfileData,
    | "designation"
    | "phone"
    | "location"
    | "about"
    | "skills"
    | "experience"
    | "education"
  >
>;

type ApiErrorResponse = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as ApiErrorResponse;

  return (
    apiError.response?.data?.message ||
    apiError.message ||
    fallback
  );
};

export const fetchCandidateProfileThunk = createAsyncThunk<
  CandidateProfileData,
  void,
  { rejectValue: string }
>(
  "candidateProfile/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCandidateProfile();
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch profile.")
      );
    }
  }
);

export const updateCandidateProfileThunk = createAsyncThunk<
  CandidateProfileData,
  ProfileUpdatePayload,
  { rejectValue: string }
>(
  "candidateProfile/update",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCandidateProfile(payload);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update profile.")
      );
    }
  }
);

export const uploadCandidateProfileVideoThunk = createAsyncThunk<
  ProfileVideoData,
  File,
  { rejectValue: string }
>(
  "candidateProfile/uploadVideo",
  async (file, { rejectWithValue }) => {
    try {
      const response = await uploadCandidateProfileVideo(file);
      return response.video;
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to upload introduction video.")
      );
    }
  }
);

export const uploadCandidateProfileResumeThunk = createAsyncThunk<
  ProfileResumeData,
  File,
  { rejectValue: string }
>(
  "candidateProfile/uploadResume",
  async (file, { rejectWithValue }) => {
    try {
      const response = await uploadCandidateProfileResume(file);
      return response.resume;
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to upload resume.")
      );
    }
  }
);
